import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { publicApi } from "@/lib/api";
import { Reveal } from "@/components/motion/Reveal";
import Seo from "@/components/seo/Seo";

const PLACEHOLDER_IMG = "https://images.unsplash.com/photo-1624957485467-f33854e1ad85?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjB0ZWFtJTIwbWVldGluZ3xlbnwwfHx8fDE3ODQ3NTQyNTJ8MA&ixlib=rb-4.1.0&q=85";

const FALLBACK_CASES = [
  { id: "f1", slug: "manufactura-iso-9001", sector: "Manufactura", norma: "ISO 9001", pais: "Costa Rica", anio: 2024, titulo: "Implementación ISO 9001 en planta de producción", empresa: "Empresa del sector manufactura", resultado: "Certificación en primer intento y −32% no conformidades.", image: PLACEHOLDER_IMG },
  { id: "f2", slug: "alimentaria-iso-14001", sector: "Industria alimentaria", norma: "ISO 14001", pais: "Costa Rica", anio: 2024, titulo: "Sistema de Gestión Ambiental ISO 14001", empresa: "Empresa del sector alimentario", resultado: "−24% en residuos, certificación en 8 meses.", image: PLACEHOLDER_IMG },
  { id: "f3", slug: "tecnologia-iso-27001", sector: "Tecnología", norma: "ISO/IEC 27001", pais: "Costa Rica", anio: 2023, titulo: "Sistema de Gestión de Seguridad de la Información", empresa: "Empresa del sector tecnológico", resultado: "Certificación y acceso a nuevos contratos.", image: PLACEHOLDER_IMG },
  { id: "f4", slug: "farma-fsc", sector: "Farmacéutica veterinaria", norma: "FSC", pais: "Panamá", anio: 2023, titulo: "Cadena de custodia FSC", empresa: "Empresa del sector farmacéutico", resultado: "Trazabilidad completa y auditoría exitosa.", image: PLACEHOLDER_IMG },
];

const SECTORES = ["Manufactura", "Farmacéutica veterinaria", "Industria alimentaria", "Servicios", "Tecnología", "Logística", "Comercio", "Sector público", "Agroindustria", "Salud", "Educación"];
const NORMAS = ["ISO 9001", "ISO 14001", "ISO/IEC 27001", "FSC", "INTE G38", "Bandera Azul Ecológica"];

export default function CasosDeExito() {
  const [items, setItems] = useState([]);
  const [params, setParams] = useSearchParams();

  const sector = params.get("sector") || "";
  const norma = params.get("norma") || "";
  const anio = params.get("anio") || "";
  const pais = params.get("pais") || "";

  useEffect(() => {
    publicApi.casos({ sector: sector || undefined, norma: norma || undefined, anio: anio || undefined, pais: pais || undefined })
      .then((r) => setItems(r.length ? r : FALLBACK_CASES))
      .catch(() => setItems(FALLBACK_CASES));
  }, [sector, norma, anio, pais]);

  const anios = useMemo(() => Array.from(new Set(items.map((i) => i.anio).filter(Boolean))).sort((a, b) => b - a), [items]);
  const paises = useMemo(() => Array.from(new Set(items.map((i) => i.pais).filter(Boolean))), [items]);

  const setFilter = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p);
  };

  return (
    <main className="pt-[72px]" data-testid="casos-index-page">
      <Seo title="Casos de éxito" description="Resultados de proyectos de implementación, auditoría y mejora de sistemas de gestión ISO en diferentes sectores." path="/casos-de-exito" />
      {/* Header */}
      <header className="border-b border-[#1A1A1A]/10 py-16 md:py-24">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10">
          <div className="eyebrow mb-6">— Índice</div>
          <h1 className="display-1">Casos de <em className="italic text-[#1B3B2B]">éxito</em>.</h1>
          <p className="mt-6 max-w-2xl text-[#4A4A4A] leading-relaxed">
            Conozca cómo hemos ayudado a diferentes organizaciones a fortalecer sus procesos, cerrar brechas, prepararse para certificaciones y alcanzar resultados sostenibles.
          </p>
        </div>
      </header>

      {/* Filters */}
      <section className="border-b border-[#1A1A1A]/10 py-6 sticky top-[72px] bg-[#F9F6F0]/85 backdrop-blur-xl z-30">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10 flex flex-wrap gap-4 items-center">
          <FilterSelect label="Sector" value={sector} onChange={(v) => setFilter("sector", v)} options={SECTORES} testId="filter-sector" />
          <FilterSelect label="Norma" value={norma} onChange={(v) => setFilter("norma", v)} options={NORMAS} testId="filter-norma" />
          <FilterSelect label="Año" value={anio} onChange={(v) => setFilter("anio", v)} options={anios} testId="filter-anio" />
          <FilterSelect label="País" value={pais} onChange={(v) => setFilter("pais", v)} options={paises} testId="filter-pais" />
          {(sector || norma || anio || pais) && (
            <button onClick={() => setParams({})} data-testid="filter-clear" className="text-xs underline text-[#4A4A4A]">Limpiar filtros</button>
          )}
        </div>
      </section>

      {/* Grid */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10">
          {items.length === 0 ? (
            <div className="text-center text-[#4A4A4A] py-24">No hay casos que coincidan con los filtros seleccionados.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {items.map((c, i) => (
                <Reveal key={c.id} delay={i * 0.06}>
                  <Link to={`/casos-de-exito/${c.slug}`} className="group block" data-testid={`caso-${i}`}>
                    <div className="relative aspect-[4/5] overflow-hidden bg-[#EFEBE2]">
                      <img src={c.image || c.imagen || PLACEHOLDER_IMG} alt={c.titulo} className="absolute inset-0 w-full h-full object-cover duotone transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                      <div className="absolute top-4 left-4 text-[10px] tracking-[0.22em] uppercase text-white bg-[#1A1A1A]/60 backdrop-blur px-3 py-1.5">{c.sector}</div>
                    </div>
                    <div className="mt-6 flex items-center gap-3 eyebrow text-[#1B3B2B]">{c.norma} · {c.anio || "—"}</div>
                    <h3 className="mt-3 font-serif text-2xl md:text-3xl leading-tight group-hover:text-[#1B3B2B] transition-colors">{c.titulo}</h3>
                    <p className="mt-2 text-sm text-[#4A4A4A]">{c.empresa}</p>
                    <div className="mt-4 text-sm text-[#1A1A1A]/85">{c.resultado}</div>
                    <div className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase link-underline">
                      Ver caso completo <ArrowUpRight className="w-3 h-3" />
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="flex items-center gap-2 text-xs text-[#4A4A4A]">
      <span className="eyebrow">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent border border-[#1A1A1A]/20 rounded-full px-3 py-1.5 text-xs focus:outline-none focus:border-[#1B3B2B]"
        data-testid={testId}
      >
        <option value="">Todos</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
